import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import JsonLD from '@/components/feature/JsonLD';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const res = await fetch('http://topshine.net.cn/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let parsed;
      try { parsed = JSON.parse(text); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
        setFormError(msg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const orgJsonLD = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫之家',
    url: 'http://topshine.net.cn/',
    description: '免费韩漫大全，好看的韩国漫画在线阅读平台，服务稳定值得信赖。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'hello@topshine.net.cn',
    },
  };

  const pageJsonLD = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 韩漫之家',
    description: '了解韩漫之家品牌故事、平台数据与商务合作信息。免费韩漫大全在线阅读平台。',
    url: 'http://topshine.net.cn/about',
    isPartOf: { '@type': 'WebSite', name: '韩漫之家', url: 'http://topshine.net.cn/' },
    about: orgJsonLD,
  };

  const faqJsonLD = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫之家是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫之家是一个专注于韩国漫画在线阅读的中文平台，提供免费韩漫大全，涵盖恋爱、奇幻、动作、搞笑等多种类型的韩国漫画。我们的漫画网址服务稳定值得信赖。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫之家的漫画是否免费？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '是的，韩漫之家提供完全免费的韩漫在线阅读服务。我们致力于为广大韩漫爱好者提供优质、稳定的阅读体验。',
        },
      },
      {
        '@type': 'Question',
        name: '如何与韩漫之家进行商务合作？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过页面下方的商务合作表单联系我们，或发送邮件至 hello@topshine.net.cn。我们期待与各类合作伙伴建立联系。',
        },
      },
    ],
  };

  return (
    <>
      <JsonLD data={[orgJsonLD, pageJsonLD, faqJsonLD]} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]} />

            {/* Hero */}
            <section className="text-center py-12 md:py-16" aria-labelledby="about-heading">
              <h1 id="about-heading" className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于韩漫之家
              </h1>
              <p className="text-base md:text-lg text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                韩漫之家是领先的免费韩漫大全平台，致力于为广大韩漫爱好者提供好看的韩国漫画在线阅读服务。我们的漫画网址服务稳定值得信赖。
              </p>
            </section>

            {/* Stats */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16" aria-label="平台数据">
              {[
                { label: '收录漫画', value: '20,000+', desc: '海量韩漫资源' },
                { label: '日活用户', value: '50万+', desc: '忠实读者群体' },
                { label: '每日更新', value: '500+', desc: '实时同步更新' },
                { label: '运营年份', value: '8年', desc: '深耕韩漫领域' },
              ].map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100">
                  <p className="text-3xl md:text-4xl font-heading font-bold text-primary-500 mb-2">{stat.value}</p>
                  <p className="text-sm font-medium text-foreground-800">{stat.label}</p>
                  <p className="text-xs text-foreground-500 mt-1">{stat.desc}</p>
                </div>
              ))}
            </section>

            {/* Brand Story */}
            <section className="mb-16" aria-labelledby="story-heading">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                <div>
                  <h2 id="story-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
                    品牌故事
                  </h2>
                  <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                    <p>
                      韩漫之家诞生于韩漫爱好者的共同愿景——让每一位喜欢韩国漫画的读者都能在一个稳定、优质的平台畅享阅读体验。我们从最初的小众社区起步，逐步发展成为中国领先的免费韩漫在线阅读平台。
                    </p>
                    <p>
                      多年来，我们始终坚持「免费、优质、稳定」的服务理念，不断扩充漫画资源库，优化阅读体验。目前平台已收录超过两万部韩国漫画作品，涵盖恋爱、奇幻、动作、搞笑、悬疑、剧情等多种类型。
                    </p>
                    <p>
                      我们拥有一支专业的运营团队，每日同步更新最新韩漫章节，确保读者第一时间看到好看的韩国漫画。韩漫之家的服务稳定值得信赖，已为数百万韩漫爱好者提供了优质的在线阅读体验。
                    </p>
                    <p>
                      未来，韩漫之家将继续深耕韩漫领域，不断优化产品与服务，为更多热爱韩国漫画的读者打造更好的阅读平台。
                    </p>
                  </div>
                </div>
                <div className="rounded-xl overflow-hidden h-72 md:h-96 bg-background-200">
                  <img
                    src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20webtoon%20studio%20illustration%20art%20workspace%2C%20cozy%20creative%20office%20with%20drawing%20tablets%20and%20art%20prints%20on%20wall%2C%20warm%20peach%20and%20cream%20tones%2C%20artistic%20atmosphere%2C%20modern%20Korean%20illustration%20studio%2C%20soft%20natural%20lighting%2C%20creative%20workspace%20with%20plants%20and%20art%20supplies&width=800&height=600&seq=about-brand-story&orientation=landscape"
                    alt="韩漫之家品牌故事"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </section>

            {/* Values */}
            <section className="mb-16" aria-labelledby="values-heading">
              <h2 id="values-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
                我们的承诺
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  {
                    icon: 'ri-shield-check-line',
                    title: '稳定可靠',
                    desc: '采用先进的技术架构，确保漫画网址访问稳定，阅读流畅不卡顿，服务稳定值得信赖。',
                  },
                  {
                    icon: 'ri-book-open-line',
                    title: '海量资源',
                    desc: '收录超过两万部韩国漫画，涵盖全类型，每日持续更新，满足您的各种阅读需求。',
                  },
                  {
                    icon: 'ri-heart-line',
                    title: '用户至上',
                    desc: '倾听每一位读者的声音，不断优化阅读体验，打造最受韩漫爱好者喜爱的在线阅读平台。',
                  },
                ].map((value) => (
                  <div key={value.title} className="p-6 rounded-xl bg-background-100 text-center">
                    <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100">
                      <i className={`${value.icon} text-2xl text-primary-600`}></i>
                    </div>
                    <h3 className="text-lg font-heading font-bold text-foreground-900 mb-2">{value.title}</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{value.desc}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section className="mb-16" aria-labelledby="contact-heading">
              <div className="max-w-2xl mx-auto">
                <h2 id="contact-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4 text-center">
                  商务合作
                </h2>
                <p className="text-sm text-foreground-500 text-center mb-8">
                  如果您有任何商务合作意向，请填写以下表单，我们的团队将尽快与您联系。
                </p>

                <form
                  id="contact-form"
                  onSubmit={handleSubmit}
                  className="bg-background-100 rounded-xl p-6 md:p-8 space-y-5"
                >
                  <input
                    type="text"
                    name="website_alt"
                    autoComplete="off"
                    tabIndex={-1}
                    aria-hidden="true"
                    readOnly
                    className="anti-spam-field"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact-name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact-email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact-company" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      公司 / 组织
                    </label>
                    <input
                      type="text"
                      id="contact-company"
                      name="company"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder="请输入您的公司或组织名称"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact-type" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      合作类型
                    </label>
                    <select
                      id="contact-type"
                      name="cooperation_type"
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors"
                    >
                      <option value="">请选择合作类型</option>
                      <option value="advertising">广告合作</option>
                      <option value="content">内容合作</option>
                      <option value="copyright">版权合作</option>
                      <option value="other">其他合作</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      合作说明 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={5}
                      maxLength={500}
                      className="w-full px-4 py-2.5 bg-background-50 border border-background-300 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                      placeholder="请描述您的合作意向和需求（限500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">限500字符</p>
                  </div>

                  {formStatus === 'error' && (
                    <div className="p-3 rounded-lg bg-primary-50 text-primary-700 text-sm">
                      {formError}
                    </div>
                  )}

                  {formStatus === 'success' && (
                    <div className="p-3 rounded-lg bg-secondary-100 text-secondary-700 text-sm">
                      提交成功！感谢您的关注，我们的团队会在 1-3 个工作日内与您联系。
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-background-50 rounded-full font-medium text-sm transition-colors whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? '提交中...' : '提交合作意向'}
                  </button>
                </form>
              </div>
            </section>

            {/* FAQ */}
            <section className="mb-16" aria-labelledby="about-faq-heading">
              <div className="max-w-2xl mx-auto">
                <h2 id="about-faq-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
                  常见问题
                </h2>
                <div className="space-y-3">
                  <details className="bg-background-100 rounded-xl p-5 group">
                    <summary className="text-sm font-medium text-foreground-800 cursor-pointer list-none flex items-center justify-between">
                      韩漫之家是什么？
                      <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180">
                        <i className="ri-arrow-down-s-line"></i>
                      </span>
                    </summary>
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                      韩漫之家是一个专注于韩国漫画在线阅读的中文平台，提供免费韩漫大全，涵盖恋爱、奇幻、动作、搞笑等多种类型的韩国漫画。我们的漫画网址服务稳定值得信赖。
                    </p>
                  </details>

                  <details className="bg-background-100 rounded-xl p-5 group">
                    <summary className="text-sm font-medium text-foreground-800 cursor-pointer list-none flex items-center justify-between">
                      韩漫之家的漫画是否免费？
                      <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180">
                        <i className="ri-arrow-down-s-line"></i>
                      </span>
                    </summary>
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                      是的，韩漫之家提供完全免费的韩漫在线阅读服务。我们致力于为广大韩漫爱好者提供优质、稳定的阅读体验，所有漫画资源均可免费浏览。
                    </p>
                  </details>

                  <details className="bg-background-100 rounded-xl p-5 group">
                    <summary className="text-sm font-medium text-foreground-800 cursor-pointer list-none flex items-center justify-between">
                      如何与韩漫之家进行商务合作？
                      <span className="w-5 h-5 flex items-center justify-center transition-transform group-open:rotate-180">
                        <i className="ri-arrow-down-s-line"></i>
                      </span>
                    </summary>
                    <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                      您可以通过上方的商务合作表单联系我们，或发送邮件至 hello@topshine.net.cn。我们期待与广告合作、内容合作、版权合作等各类合作伙伴建立联系。
                    </p>
                  </details>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}